import { onUnmounted, ref, watch } from "vue"
import type { Ref } from "vue"

type DrawerScreen = "list" | "detail"

interface UseMobileDrawerBackNavOptions {
  isOpen: Ref<boolean>
  screen: Ref<DrawerScreen>
  /** Only pushes/consumes history entries while true — desktop Dialogs never call this. */
  enabled: Ref<boolean>
}

type PopHandler = () => void

// Shared across *every* Drawer using this composable — a single hardware/gesture back press only
// ever pops one real history entry, so exactly one owner's handler must run for it. Each mounted
// instance used to register its own independent `popstate` listener and decide what to do based
// on its own local depth — which mis-fires as soon as two Drawers are nested (e.g.
// a settings Drawer stacked inside the profile Drawer): a back press meant to step
// the inner Drawer from "Чат" back to its own section list also reached the outer Drawer's
// listener, which — having no way to tell the pop wasn't "its" entry — closed itself too. Routing
// through one shared, LIFO-ordered stack means only whichever Drawer most recently pushed an
// entry reacts to the next pop, matching the real browser stack exactly.
const popHandlers: PopHandler[] = []
let popStateListenerInstalled = false

function ensurePopStateListener() {
  if (popStateListenerInstalled || typeof window === "undefined") return
  popStateListenerInstalled = true
  window.addEventListener("popstate", () => {
    popHandlers.pop()?.()
  })
}

// Mirrors a mobile Drawer's list -> detail navigation onto the browser History API, so the
// Android hardware/gesture back button — which Tauri's generated `WryActivity.kt` wires straight
// to `WebView.goBack()` — steps back through it like a native screen stack: one press from the
// detail pane returns to the section list, the next press closes the Drawer, instead of leaving
// the app (or doing nothing) via a "canGoBack() == false" navigation.
//
// Deliberately only ever *pushes* or *replaces* history entries itself — it never calls
// `history.go()`/`history.back()` at all. `watch()` callbacks flush on a microtask while
// `history.go()` resolves asynchronously on a later task, so calling it eagerly from a "close
// programmatically" branch (X button, backdrop click, or — critically — right as the Drawer's own
// NuxtLink navigates elsewhere) can fire *after* a real route push has already landed on top of
// this Drawer's entry, popping that real navigation instead and bouncing the user straight back
// out of the page they just opened. Any entry this composable can no longer safely reclaim is
// just abandoned in the stack (and in `popHandlers`) instead — its handler simply fires, a no-op
// by then, whenever that entry eventually *is* popped for real, instead of ever popping (or
// reacting to the pop of) the wrong entry.
export function useMobileDrawerBackNav({ isOpen, screen, enabled }: UseMobileDrawerBackNavOptions) {
  if (typeof window === "undefined") return
  ensurePopStateListener()

  // How many of *this Drawer's* entries are believed to still be sitting in the real history
  // stack (and thus also have a handler on `popHandlers`): 0 closed, 1 list, 2 detail.
  let depth = 0
  // Set while reacting to a popstate (browser/hardware back) so the isOpen/screen writes below
  // don't loop back into pushing another entry for the same navigation.
  let syncingFromPopState = false

  // Vue Router's own `createWebHistory()` keeps its bookkeeping (`position`, `back`, `forward`,
  // `replaced`, `scroll`, ...) in `history.state` — replacing that object wholesale (instead of
  // merging into it) corrupts the router's internal position tracking. A later router navigation
  // then reads garbage back out of `history.state` and can hand the *native* pushState/replaceState
  // call a malformed argument, which some WebViews reject outright with a raw
  // `SyntaxError: The string did not match the expected pattern.` Always spread the existing state
  // in first so whatever else (the router, Sentry, anything) already stored there survives.
  //
  // WebView History implementations also vary in other ways (rate limits on pushState, quirks with
  // no more entries, etc.) — never let a failure here break rendering of the Drawer itself.
  function safePushState(newDepth: number, onPop: PopHandler) {
    try {
      history.pushState({ ...history.state, mobileDrawerNav: true, depth: newDepth }, "")
      popHandlers.push(onPop)
      depth = newDepth
    } catch (e) {
      console.error("useMobileDrawerBackNav: history.pushState failed", e)
    }
  }

  // `immediate: true` matters here: `EditGroupDialog.vue`/etc. mount
  // this composable *before* the user ever opens their Drawer (the component itself is always
  // rendered, only the `open` prop toggles later), so a plain `watch()` correctly catches the
  // false -> true transition. `BotDetailPanel.vue` (mobile) instead lives *inside* vaul's
  // `DrawerContent`, which only mounts its slot content once `open` is already `true` — so this
  // composable's very first run already sees `isOpen.value === true` with no transition to
  // observe, the initial history entry never gets pushed, `depth` stays 0 forever, and the very
  // next hardware back press finds nothing of ours on the stack — it just falls straight through
  // to real app-level navigation and kicks the user out of the Drawer entirely. `immediate: true`
  // makes the composable check "is this Drawer already open right now?" on mount too, not just
  // "did it just open?", so it still pushes correctly no matter which mounting pattern applies.
  watch(isOpen, (open) => {
    if (syncingFromPopState || !enabled.value) return
    if (open && depth === 0) {
      safePushState(1, () => {
        syncingFromPopState = true
        depth = 0
        isOpen.value = false
        syncingFromPopState = false
      })
    } else if (!open && depth > 0) {
      // Closed some other way than a back press — see the note above for why this must not try
      // to pop anything off the real history (or off `popHandlers`).
      depth = 0
    }
  }, { immediate: true })

  watch(screen, (value, previous) => {
    if (syncingFromPopState || !enabled.value || !isOpen.value) return
    if (value === "detail" && previous === "list" && depth === 1) {
      safePushState(2, () => {
        syncingFromPopState = true
        depth = 1
        screen.value = "list"
        syncingFromPopState = false
      })
    } else if (value === "list" && previous === "detail" && depth === 2) {
      // The user hit our own in-Drawer back-arrow rather than the hardware back button — collapse
      // the detail entry back down in place (`replaceState`, not a pop) so the browser stack
      // doesn't grow unboundedly, and swap in the "close" handler so this Drawer's *next* real
      // back press correctly closes it instead of re-running the (now stale) "detail -> list"
      // handler. Assumes nothing else pushed on top in between, which holds in practice since
      // only one Drawer/dialog is ever mid-interaction at a time.
      try {
        history.replaceState({ ...history.state, mobileDrawerNav: true, depth: 1 }, "")
        popHandlers[popHandlers.length - 1] = () => {
          syncingFromPopState = true
          depth = 0
          isOpen.value = false
          syncingFromPopState = false
        }
        depth = 1
      } catch (e) {
        console.error("useMobileDrawerBackNav: history.replaceState failed", e)
      }
    }
  })

  onUnmounted(() => {
    depth = 0
  })
}

interface UseMobileDrawerCloseOnBackOptions {
  isOpen: Ref<boolean>
  /** Only pushes/consumes history entries while true — desktop Dialogs never call this. */
  enabled: Ref<boolean>
}

// Single-screen variant of useMobileDrawerBackNav, for Drawers with no list -> detail navigation
// of their own (e.g. InviteDialog.vue, MobileNewGroupDrawer.vue) — one back press just closes the
// Drawer instead of leaving the app.
export function useMobileDrawerCloseOnBack({ isOpen, enabled }: UseMobileDrawerCloseOnBackOptions) {
  const screen = ref<DrawerScreen>("list")
  useMobileDrawerBackNav({ isOpen, screen, enabled })
}

interface UseMobileDrawerDepthBackNavOptions {
  isOpen: Ref<boolean>
  /** How many screens deep the Drawer is: 0 its root screen, 1 a page opened from it, and so on. */
  depth: Ref<number>
  /** Only pushes/consumes history entries while true — desktop Dialogs never call this. */
  enabled: Ref<boolean>
}

// Multi-level variant of useMobileDrawerBackNav, for Drawers whose pages open further pages of their
// own (MobileProfileDrawer.vue: root -> "Настройки" -> "Оформление"). Same history rules as above —
// one entry per level, only ever pushed or replaced, never popped by us — but each entry's handler
// sets an *absolute* level rather than stepping one back, so an entry left behind by the in-Drawer
// back arrow/swipe (see the `replaceState` note above) can't step the Drawer back twice.
export function useMobileDrawerDepthBackNav({ isOpen, depth, enabled }: UseMobileDrawerDepthBackNavOptions) {
  if (typeof window === "undefined") return
  ensurePopStateListener()

  // How many of this Drawer's entries are believed to be on the real history stack: 0 while closed,
  // `depth + 1` while open.
  let pushed = 0

  // Popping entry `level` (1-based) takes the Drawer back to what it was before that entry was pushed.
  function backFrom(level: number): PopHandler {
    return () => {
      pushed = level - 1
      if (level === 1) isOpen.value = false
      else depth.value = level - 2
    }
  }

  function writeState(level: number, mode: "push" | "replace"): boolean {
    try {
      const state = { ...history.state, mobileDrawerNav: true, depth: level }
      if (mode === "push") history.pushState(state, "")
      else history.replaceState(state, "")
      return true
    } catch (e) {
      console.error(`useMobileDrawerDepthBackNav: history.${mode}State failed`, e)
      return false
    }
  }

  watch([isOpen, depth], ([open, currentDepth]) => {
    if (!enabled.value) return
    const target = open ? currentDepth + 1 : 0
    if (target > pushed) {
      while (pushed < target) {
        if (!writeState(pushed + 1, "push")) return
        popHandlers.push(backFrom(pushed + 1))
        pushed += 1
      }
    } else if (target < pushed) {
      if (target === 0) {
        // Closed some other way than a back press — abandoned, see useMobileDrawerBackNav above.
        pushed = 0
        return
      }
      // Went back with the in-Drawer arrow/swipe: turn the top entry into this level's in place.
      if (!writeState(target, "replace")) return
      popHandlers[popHandlers.length - 1] = backFrom(target)
      pushed = target
    }
  }, { immediate: true })

  onUnmounted(() => {
    pushed = 0
  })
}
